import { Link, useLocation } from 'react-router-dom'
import type { OrgRole } from '@/api'
import { DefinitionList } from '@/components/data'
import type { Definition } from '@/components/data'
import { buttonShape } from '@/components/primitives'
import { roleLabel, rolesWith } from '@/screens/Auth/roles'
import { DeadEnd } from '@/screens/DeadEnd/DeadEnd'
import { useAuthStore } from '@/store/authStore'
import type { Permission } from '@/store/permissions'

/**
 * What a signed-in person sees when their role does not include this screen.
 *
 * Rendered in place rather than redirected away from, and that is the decision
 * worth writing down. Bouncing somebody silently to the dashboard makes the
 * console look broken: they clicked a link a colleague sent them, the address
 * bar changed to something else, and nothing said why. It also destroys the URL
 * they were trying to reach, so they cannot forward it to whoever can open it.
 *
 * ## It says what would be needed, not only that this is refused
 *
 * "Forbidden" is a dead end. This screen names three things instead: the role
 * the person holds, which roles hold the permission the screen wanted, and who
 * can change a role. Those are the three facts standing between somebody and
 * the next thing they were going to do, and each one is otherwise a message to
 * a colleague who may be asleep.
 *
 * Both lists of roles are derived from `store/permissions.ts` rather than
 * written out here. Copy that names roles by hand goes stale the day somebody
 * widens a permission, and it goes stale silently, on a screen nobody on the
 * team ever looks at on purpose.
 *
 * ## The permission is a prop, and the route guard cannot pass one
 *
 * `RequirePermission` in `routes.tsx` renders `<NoAccess />` with no props,
 * because at that point it knows only that a check failed. So the prop is
 * optional and the screen degrades to the role and the address, which is still
 * more than "forbidden". A screen refusing part of itself in place, a publish
 * action a manager may take and an analyst may not, can render this with the
 * permission and get the specific version.
 *
 * ## It is not a security boundary
 *
 * The server refuses the request whatever this renders, and reaching across
 * organizations is reported as not found rather than as forbidden, because
 * confirming that an id exists is itself a small leak. All this decides is
 * whether somebody is shown a door that would not open.
 */

export interface NoAccessProps {
  /** The permission the screen wanted, where whatever refused knows it. */
  permission?: Permission
}

export function NoAccess({ permission }: NoAccessProps) {
  const role = useAuthStore((state) => state.user?.role ?? null)
  const location = useLocation()
  const address = location.pathname + location.search

  if (role === null) {
    return (
      <DeadEnd
        title="You do not have access to this"
        actions={
          <Link className={buttonShape({ variant: 'primary' })} to="/login">
            Go to sign in
          </Link>
        }
      >
        <p>
          Nobody is signed in on this browser, so there is no role to check. Sign in and open the
          address again.
        </p>
      </DeadEnd>
    )
  }

  const holders = permission === undefined ? null : rolesWith(permission)
  /*
   * Who can change a role is derived too. The obvious sentence is "ask an owner
   * or an admin", and it is true only for as long as `staff.update` is held by
   * exactly those two.
   */
  const changers = phrase(rolesWith('staff.update'))

  const facts: Definition[] = [{ term: 'Your role', description: roleLabel(role) }]
  if (holders !== null && holders.length > 0) {
    facts.push({ term: 'Roles that can open this', description: phrase(holders) })
  }
  facts.push({
    term: 'The address',
    description: address,
    hint: 'Send this to whoever can open it, rather than describing where you were.',
  })

  return (
    <DeadEnd
      title="You do not have access to this"
      facts={<DefinitionList items={facts} />}
      actions={
        <Link className={buttonShape({ variant: 'primary' })} to="/dashboard">
          Back to Today
        </Link>
      }
    >
      <p>
        {holders === null
          ? `Your role in this organization is ${roleLabel(role).toLowerCase()}, and this screen is not part of it.`
          : `Your role in this organization is ${roleLabel(role).toLowerCase()}. This screen needs ${phrase(holders)}.`}
      </p>
      <p>
        {`If you need it, ask ${changers} in your organization. Roles are changed on the Team screen, every change is written to the audit log, and nobody can raise their own.`}
      </p>
    </DeadEnd>
  )
}

/**
 * Roles as prose: "an owner, an admin, or a manager".
 *
 * `Intl.ListFormat` rather than `join(', ')`, because the separator and the
 * final conjunction are language-specific and this sentence is among the first
 * things that will be translated. `disjunction` is the right type: any one of
 * these roles is enough, and "and" would read as needing all of them at once.
 */
function phrase(roles: readonly OrgRole[]): string {
  const names = roles.map((role) => withArticle(roleLabel(role).toLowerCase()))
  return new Intl.ListFormat('en', { type: 'disjunction' }).format(names)
}

/** "an admin", "a manager". Crude, and correct for the six words it ever sees. */
function withArticle(word: string): string {
  return /^[aeiou]/.test(word) ? `an ${word}` : `a ${word}`
}
